import {AjaxP, Module} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import Tracking2 from '../../ts/services/tracking2';

import type {TeaserData, Teaser} from './ITeaserData';

export default class Infinitescroll extends Module {
  public infiniteURI: string;
  public infiniteURIParameters: string | undefined;
  public isLoading: boolean;
  public infiniteContent: Teaser[];

  private readonly triggerPointThreshold: number;
  private readonly items: ChildNode[];

  private observer: IntersectionObserver;
  private maxTeasersToShow: number;
  private teasersShown: number;
  private teaserRequested: boolean;
  private newItems: ChildNode[];
  private columns: HTMLElement[];
  private currentColumn: number;
  private placeHolderMarkup: HTMLElement;
  private halfwayScrollReached: boolean;

  /** Init. from DOM. */
  static attachTo(root: HTMLElement): Infinitescroll {
    return new Infinitescroll(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'infinitescroll';

    this.maxTeasersToShow = 70;

    // Amount of pixels from the bottom of the page to reach in order to show the next teasers
    this.triggerPointThreshold = 300;

    // How many teasers have been shown so far
    this.teasersShown = 0;

    // The list of teasers to be shown next, in the given order
    this.infiniteContent = [];

    // True when the data containing the teasers is being requested from the backend
    this.isLoading = false;

    // Wether the teaser data has been requested or not
    this.teaserRequested = false;

    // List of all teasers generated. Necessary when re-calculating layout
    this.items = [];

    // List of new items to generate and append in the DOM
    this.newItems = [];

    // The layout columns holding the teasers
    this.columns = [];

    // To append each new item in a subsequent column
    this.currentColumn = 0;

    // True when (this.maxTeasersToShow / 2) teasers have been shown
    this.halfwayScrollReached = false;
  }

  /** Start the module. */
  start() {
    if (!this.root_.dataset.uri) {
      this.publish('sentry:log', {error: 'no infiniteURI!'});
      return;
    }

    this.infiniteURI = this.root_.dataset.uri;
    this.infiniteURIParameters = this.root_.dataset.uriParams;
    this.placeHolderMarkup = this.root_.getElementsByClassName('infiniteScrollWrapper')[0] as HTMLElement;

    this.observeOwnVisibility();
    this.createColumns();

    // Show teasers on window scroll
    this.subscribe('windowScrolling', () => {
      if (this.triggerPointReached()) {
        this.showNextTeasers();
      }
    });

    // Re-calculate layout on viewport width change, and only if the number of columns has to change
    this.subscribe('window:resize', () => {
      if (this.columns.length !== this.getColumnCount()) {
        this.createColumns();
        this.fillColumns(this.items);
      }
    });

    // Show teasers if we already reached the trigger point
    if (this.triggerPointReached()) {
      this.showNextTeasers();
    }
  }

  observeOwnVisibility() {
    const callback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.updateScrollProgress(0);
          Tracking2.callTealiumView({contentTypeExtension: '.lazyload'});
          this.observer.disconnect();
        }
      });
    };
    const options = {threshold: 0.14}; // displays roughly the first teasers
    this.observer = new IntersectionObserver(callback, options);
    this.observer.observe(this.root_);
  }

  /** Determines whether the trigger point has been reached or not */
  triggerPointReached(): boolean {
    const contentHeight = document.body.offsetHeight;
    const windowHeight = window.innerHeight;

    if (contentHeight <= windowHeight) {
      return true;
    } else {
      const loadPoint = windowHeight + window.scrollY + this.triggerPointThreshold;
      return loadPoint >= contentHeight;
    }
  }

  /** Creates the appropriate number of columns for the current viewport */
  createColumns() {
    try {
      // Remove existing columns from DOM
      while (this.placeHolderMarkup.firstElementChild) {
        this.placeHolderMarkup.removeChild(this.placeHolderMarkup.firstElementChild);
      }

      // Reset layout variables
      this.columns = [];
      this.currentColumn = 0;

      let columnsNeeded = this.getColumnCount();
      const columnFragment = document.createDocumentFragment();

      // Create columns
      while (columnsNeeded--) {
        const column = document.createElement('div');
        column.classList.add('column');
        columnFragment.appendChild(column);
        this.columns.push(column);
      }

      this.placeHolderMarkup.appendChild(columnFragment);
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  getColumnCount(): number {
    switch (Viewport.getCurrentViewport()) {
      case 'large1920':
      case 'large1680':
      case 'large1440':
      case 'large':
        return 3;
      case 'desktop':
      case 'tablet':
        return 2;
      default:
        return 1;
    }
  }

  /** Show more teasers. If no more teasers are available, fetch new ones first. */
  showNextTeasers() {
    if (this.isLoading || this.teasersShown === this.maxTeasersToShow) {
      return;
    }

    try {
      // Fetch teasers if we don't have already, otherwise append the ones we have
      if (!this.teaserRequested) {
        this.fetchTeaserData();
      } else {
        this.appendNewTeasers();
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  fetchTeaserData() {
    try {
      let url = this.generateRequestURI();
      this.log('URL: ' + url);
      this.isLoading = true;
      this.teaserRequested = true;

      if (Viewport.matches('tablet')) {
        this.subscribe('newheader:leftColReady', leftColFirst5 => {
          const leftColFirst5Str = leftColFirst5 as string;
          if (leftColFirst5Str) {
            url += '&excludeIds=' + leftColFirst5Str;
          }
          url += this.getAdditionalExcludeIds();
          this.getTeaserData(url);
        });
        this.publish('infinitescroll:pingInfScroll', {});
      } else {
        this.getTeaserData(url);
      }
    } catch (e) {
      this.logUnknownError(e, true);
      this.isLoading = false;
      this.teaserRequested = true;
    }
  }

  /** [CADNPCA-8852] Additional used corem-ids */
  getAdditionalExcludeIds(): string {
    const uiDups = window.ui.duplication;
    return (uiDups?.usedContentIds) ? `,${uiDups.usedContentIds.join(',')}` : '';
  }

  generateRequestURI(): string {
    const requestURL = new URL(this.infiniteURI, window.location.origin);

    if (this.infiniteURIParameters) {
      requestURL.search = this.infiniteURIParameters;
    }
    return requestURL.toString();
  }

  getTeaserData(teaserDataUrl: string) {
    AjaxP.getJSON(teaserDataUrl).then(data => {
      this.isLoading = false;
      this.storeNewTeaserDataInMemory(data as TeaserData);
    }).catch(() => {
      this.isLoading = false;
      this.log('No infinite content available');
      this.teaserRequested = true;
    });
  }

  storeNewTeaserDataInMemory(jsonTeaserData: TeaserData) {
    try {
      const teasers = jsonTeaserData.infinite;
      this.infiniteContent = this.infiniteContent.concat(teasers);

      if (this.infiniteContent.length < this.maxTeasersToShow) {
        this.maxTeasersToShow = this.infiniteContent.length;
      }

      this.showNextTeasers();
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Appends new teaser markup to the appropriate container */
  appendNewTeasers() {
    try {
      for (let n = 0, total = this.totalTeasersToShowNext(); n < total; n++) {
        // Create a Node from the markup string and append it to the DOM
        const teaserNode = document.createElement('div');
        const infinite = this.infiniteContent.shift();

        if (infinite) {
          teaserNode.innerHTML = infinite.teaser;

          if (teaserNode.firstChild) {
            // Store the item
            this.items.push(teaserNode.firstChild);
            this.newItems.push(teaserNode.firstChild);

            this.teasersShown += 1;

            if (this.infiniteContent.length === 0 || this.teasersShown === this.maxTeasersToShow) {
              break;
            }
          }
        }
      }

      // Fill columns with the new items
      this.fillColumns(this.newItems);
      this.newItems = [];

      // Initialize dynamic content
      this.publish('mod:dynInit', this.placeHolderMarkup);
      // initialize web component in infinite scroll
      this.publish('webcomponent:dynInit', this.placeHolderMarkup);

      // CADNPCA-7121 init teaser brain tracking
      this.publish('general:newContent', this.placeHolderMarkup);
      this.initializeAds(this.placeHolderMarkup);

      if ((this.teasersShown >= this.maxTeasersToShow / 2) && !this.halfwayScrollReached) {
        this.halfwayScrollReached = true;
        this.updateScrollProgress(50);
      }

      if (this.teasersShown === this.maxTeasersToShow) {
        this.updateScrollProgress(100);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Determine how many teasers should be shown next, based on viewport size */
  totalTeasersToShowNext(): number {
    switch (Viewport.getCurrentViewport()) {
      case 'large1920':
      case 'large1680':
        return 8;
      case 'large1440':
      case 'large':
        return 6;
      case 'desktop':
        return 4;
      case 'tablet':
        return 6;
      case 'mobile':
        return 5;
      default:
        return 2;
    }
  }

  /** Fill columns with the provided list of items */
  fillColumns(newColumnItems: ChildNode[]): void {
    try {
      const totalColumns = this.columns.length;
      const columnFragments = [] as DocumentFragment[];

      // Create column fragments to avoid recalculating styles for each teaser
      for (let n = 0; n < totalColumns; n++) {
        columnFragments[n] = document.createDocumentFragment();
      }

      newColumnItems.forEach(item => {
        // Place the item in the right column
        columnFragments[this.currentColumn].appendChild(item);

        // Advance to the next column. Go back to zero after reaching the last column
        this.currentColumn = (this.currentColumn + 1) % totalColumns;
      });

      // Append column fragments in the DOM
      for (let n = 0; n < totalColumns; n++) {
        this.columns[n].appendChild(columnFragments[n]);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  /** Initializes dynamic ads */
  initializeAds(topnode: Element) {
    try {
      let adFoundCounter = 0;
      const adInfo = [] as Element[];

      if (!topnode) {
        return;
      }

      topnode.querySelectorAll('.js-co-dynamic:not([data-processed="true"])').forEach(node => {
        node.setAttribute('data-ignore', 'true');
        node.setAttribute('data-processed', 'true');
        adInfo.push(node);
        adFoundCounter++;
      });

      if (adFoundCounter > 0) {
        this.publish('dynamicAdsNeeded', adInfo);
      }
    } catch (e) {
      this.logUnknownError(e, true);
    }
  }

  updateScrollProgress(progress: 0 | 50 | 100) {
    const emptyClicks = {
      click1: 'undefined',
      click2: 'undefined',
      click3: 'undefined',
      click4: 'undefined'
    };

    const clickNames = {
      0: 'lazyload_0_viewed',
      50: 'lazyload_50_viewed',
      100: 'lazyload_100_viewed'
    };

    this.publish('content:infinitescrollScrolling', Object.assign(emptyClicks, {
      clickname: clickNames[progress]
    }));
  }
}
